// ---------------------------------------------------------------------------
// PLACEHOLDER DATA — there is no backend endpoint for station validation yet.
//
// What is real vs. placeholder:
//   * Station IDs, names, Tmax bias and MAE: copied from the Jan–Sep 2026
//     "Daily maximum temperature, station (FW21) vs FNN grid" analysis.
//   * KBDI mean differences (P / T / P+T): copied from the "KBDI with inputs
//     from the FNN grid" analysis, part 1 (north stations). Southern stations
//     (part 2) were not provided, so they get estimated placeholder values.
//   * Station lat/lon, elevation and agency: REAL, read from
//     ./stations/raws_loc.csv (matched on the RAWS id, e.g. "fsum").
//   * All daily time series: SYNTHETIC, generated from a seeded RNG so the
//     charts are stable between renders. They only mimic the shape of the
//     real plots and must be replaced with backend data.
// ---------------------------------------------------------------------------

import type { StationLocation } from "./stationLocations";

export const IS_PLACEHOLDER_DATA = true;

export interface KbdiMeanDiff {
    p: number;   // precip from grid, Tmax from FEMS
    t: number;   // Tmax from grid, precip from FEMS
    pt: number;  // both from grid
}

export interface StationMeta {
    id: string;       // NWS/FEMS station number, e.g. "82002"
    rawsId: string;   // RAWS id in raws_loc.csv, e.g. "fsum"
    name: string;
    lat: number;
    lon: number;
    elevation: number | null; // metres
    agency?: string;
    tmaxBias: number; // grid minus station, °F
    tmaxMae: number;  // °F
    kbdi: KbdiMeanDiff;
    kbdiEstimated: boolean; // true = KBDI numbers are placeholders too
}

type RawStation = Pick<StationMeta, "id" | "rawsId" | "name" | "tmaxBias" | "tmaxMae"> & { kbdi?: KbdiMeanDiff };

const RAW_STATIONS: RawStation[] = [
    { id: "82002", rawsId: "fsum", name: "Sumatra", tmaxBias: -3.9, tmaxMae: 4.0, kbdi: { p: -48, t: -78, pt: -129 } },
    { id: "82201", rawsId: "fsan", name: "Sanborn", tmaxBias: -3.4, tmaxMae: 3.5, kbdi: { p: -62, t: -79, pt: -149 } },
    { id: "83703", rawsId: "flwr", name: "Lake Woodruff", tmaxBias: -3.3, tmaxMae: 3.3, kbdi: { p: -7, t: -47, pt: -55 } },
    { id: "86401", rawsId: "foas", name: "Oasis", tmaxBias: -3.2, tmaxMae: 3.2 },
    { id: "83502", rawsId: "flkg", name: "Lake George", tmaxBias: -3.1, tmaxMae: 3.1, kbdi: { p: -32, t: -54, pt: -93 } },
    { id: "86501", rawsId: "fnkd", name: "National Key Deer NW", tmaxBias: -3.0, tmaxMae: 3.0 },
    { id: "84802", rawsId: "flkw", name: "Lake Wales", tmaxBias: -2.9, tmaxMae: 2.9 },
    { id: "80802", rawsId: "fblo", name: "Bloxham", tmaxBias: -2.8, tmaxMae: 2.9, kbdi: { p: -70, t: -70, pt: -148 } },
    { id: "85801", rawsId: "fbri", name: "Brighton", tmaxBias: -2.8, tmaxMae: 2.8 },
    { id: "81301", rawsId: "folu", name: "Olustee", tmaxBias: -2.6, tmaxMae: 2.7, kbdi: { p: -32, t: -51, pt: -84 } },
    { id: "83501", rawsId: "fcen", name: "Central", tmaxBias: -2.6, tmaxMae: 2.6, kbdi: { p: -81, t: -46, pt: -123 } },
    { id: "84001", rawsId: "fpai", name: "Paisley", tmaxBias: -2.5, tmaxMae: 2.5, kbdi: { p: -60, t: -35, pt: -103 } },
    { id: "81302", rawsId: "fedd", name: "Eddy Tower", tmaxBias: -2.5, tmaxMae: 2.5, kbdi: { p: -26, t: -45, pt: -75 } },
    { id: "86402", rawsId: "fmil", name: "Miles City RAWS", tmaxBias: -2.5, tmaxMae: 2.5 },
    { id: "86406", rawsId: "fpaw", name: "Panther West", tmaxBias: -2.5, tmaxMae: 2.5 },
    { id: "82001", rawsId: "fwil", name: "Wilma", tmaxBias: -2.3, tmaxMae: 2.4, kbdi: { p: -11, t: -56, pt: -66 } },
    { id: "86704", rawsId: "fche", name: "Chekika", tmaxBias: -2.3, tmaxMae: 2.3 },
    { id: "86702", rawsId: "fcac", name: "Cache", tmaxBias: -2.2, tmaxMae: 2.3 },
    { id: "86301", rawsId: "fhon", name: "Honeymoon RAWS", tmaxBias: -2.2, tmaxMae: 2.2 },
    { id: "86001", rawsId: "flox", name: "Loxahatchee", tmaxBias: -2.1, tmaxMae: 2.1 },
    { id: "86405", rawsId: "fpae", name: "Panther East", tmaxBias: -1.9, tmaxMae: 1.9 },
    { id: "83001", rawsId: "flsu", name: "Lower Suwannee", tmaxBias: -1.7, tmaxMae: 1.9, kbdi: { p: -57, t: -26, pt: -81 } },
    { id: "84402", rawsId: "fmer", name: "Merritt Island", tmaxBias: -1.7, tmaxMae: 1.8, kbdi: { p: 8, t: -30, pt: -23 } },
    { id: "86404", rawsId: "frac", name: "Raccoon Point RAWS", tmaxBias: -1.2, tmaxMae: 1.5 },
    { id: "86403", rawsId: "foch", name: "Ochopee RAWS", tmaxBias: -0.9, tmaxMae: 1.2 },
    { id: "82203", rawsId: "fstw", name: "St. Marks (West)", tmaxBias: -0.8, tmaxMae: 1.4, kbdi: { p: -28, t: -22, pt: -47 } },
    { id: "82202", rawsId: "fstm", name: "St. Marks (East)", tmaxBias: -0.3, tmaxMae: 1.7, kbdi: { p: -8, t: -16, pt: -23 } },
    { id: "85301", rawsId: "favo", name: "Avon Park AF Range 1", tmaxBias: 0.1, tmaxMae: 1.7 },
    { id: "86102", rawsId: "gdin", name: "Ding Darling NWR", tmaxBias: 0.4, tmaxMae: 1.4 },
    { id: "80202", rawsId: "fnav", name: "Naval Live Oaks", tmaxBias: 3.6, tmaxMae: 4.7, kbdi: { p: 95, t: 50, pt: 146 } },
];

// Coordinates come from raws_loc.csv (loaded at runtime, see stationLocations.ts).
// Stations missing from the CSV are dropped (with a console warning) rather
// than drawn in a made-up place.
// Southern stations (part 2 of the KBDI analysis) were not provided:
// derive a rough placeholder from the Tmax bias so the map still has values.
export function buildStations(rawsLocations: StationLocation[]): StationMeta[] {
    const byId = new Map(rawsLocations.map(r => [r.id, r]));
    return RAW_STATIONS.flatMap((s): StationMeta[] => {
        const loc = byId.get(s.rawsId);
        if (!loc) {
            console.warn(`Station ${s.id} (${s.rawsId}) not found in raws_loc.csv; skipping.`);
            return [];
        }
        const base = { ...s, lat: loc.lat, lon: loc.lon, elevation: loc.elevation, agency: loc.agency };
        if (s.kbdi) return [{ ...base, kbdi: s.kbdi, kbdiEstimated: false }];
        const t = Math.round(s.tmaxBias * 15);
        const p = Math.round(t * 0.9);
        return [{ ...base, kbdi: { p, t, pt: Math.round((p + t) * 1.1) }, kbdiEstimated: true }];
    });
}

// RAWS ids used by the validation set, so the map can show the other RAWS separately.
export const VALIDATION_RAWS_IDS = new Set(RAW_STATIONS.map(s => s.rawsId));

// Overall summary from the analysis (median difference from the all-FEMS run)
export const KBDI_SUMMARY = [
    { inputs: "Precip", bias: -32.0, mae: 39.0 },
    { inputs: "Tmax", bias: -34.8, mae: 34.8 },
    { inputs: "Precip + Tmax", bias: -70.8, mae: 78.1 },
];

// ---------------------------------------------------------------------------
// Synthetic daily series (Jan 1 – Sep 30, 2026)
// ---------------------------------------------------------------------------

const START_UTC = Date.UTC(2026, 0, 1);
const N_DAYS = 273;
export const DATES: Date[] = Array.from({ length: N_DAYS }, (_, i) => new Date(START_UTC + i * 86_400_000));

export interface StationSeries {
    tmaxStation: (number | null)[];
    tmaxGrid: (number | null)[];
    kbdiP: number[];
    kbdiT: number[];
    kbdiPT: number[];
}

const mulberry32 = (seed: number) => () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const gauss = (rng: () => number) => {
    const u = Math.max(rng(), 1e-9);
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rng());
};

const round1 = (v: number) => Math.round(v * 10) / 10;

const seriesCache = new Map<string, StationSeries>();

export function getStationSeries(s: StationMeta): StationSeries {
    const cached = seriesCache.get(s.id);
    if (cached) return cached;

    const rng = mulberry32(Number(s.id));
    const seasonal = (d: number) => Math.cos((2 * Math.PI * (d - 15)) / 365); // 1 in mid-Jan, -1 in mid-Jul

    // --- Tmax ---
    const mean = 82 + (25 - s.lat) * 0.6;
    const amp = 8 + (s.lat - 24.5) * 1.6;
    const errSd = Math.max(0.6, Math.sqrt(Math.max(0, s.tmaxMae ** 2 - s.tmaxBias ** 2)) * 1.2 + 0.6);

    const tmaxStation: (number | null)[] = [];
    const tmaxGrid: (number | null)[] = [];
    let anomaly = 0;
    for (let d = 0; d < N_DAYS; d++) {
        const winter = (1 + seasonal(d)) / 2;
        anomaly = 0.6 * anomaly + gauss(rng) * (2 + 4 * winter * (amp / 15));
        const station = mean - amp * seasonal(d) + anomaly;
        const grid = station + s.tmaxBias + gauss(rng) * errSd;
        const missing = rng() < 0.02;
        tmaxStation.push(missing ? null : round1(station));
        tmaxGrid.push(round1(grid));
    }

    // --- KBDI differences ---
    const ramp = (i: number) => i / (N_DAYS - 1);

    // T: steady drift, mean ≈ s.kbdi.t
    let wiggle = 0;
    const kbdiT = DATES.map((_, i) => {
        wiggle = 0.9 * wiggle + gauss(rng) * 0.8;
        return 2 * s.kbdi.t * ramp(i) + wiggle;
    });

    // P: jumps at rain events (more frequent in summer), rescaled to mean ≈ s.kbdi.p
    const sign = s.kbdi.p >= 0 ? 1 : -1;
    let level = 0;
    const rawP = DATES.map((_, d) => {
        const summer = (1 - seasonal(d)) / 2;
        if (rng() < 0.03 + 0.09 * summer) {
            const size = -Math.log(Math.max(rng(), 1e-9)) * 25;
            level += rng() < 0.25 ? -sign * size * 0.6 : sign * size;
        }
        level *= 0.995;
        return level;
    });
    const rawMean = rawP.reduce((a, b) => a + b, 0) / N_DAYS;
    const scale = Math.abs(rawMean) < 1e-6 ? 0 : s.kbdi.p / rawMean;
    const kbdiP = rawP.map(v => v * scale);

    // P+T: roughly additive plus extra drying-calculation effect
    const extra = s.kbdi.pt - s.kbdi.p - s.kbdi.t;
    const kbdiPT = DATES.map((_, i) => kbdiP[i] + kbdiT[i] + 2 * extra * ramp(i));

    const out: StationSeries = {
        tmaxStation,
        tmaxGrid,
        kbdiP: kbdiP.map(round1),
        kbdiT: kbdiT.map(round1),
        kbdiPT: kbdiPT.map(round1),
    };
    seriesCache.set(s.id, out);
    return out;
}